/**
 * Звук примитивов: единый граф Web Audio.
 *
 * ПОЧЕМУ НЕ `el.volume`. Громкость на самом элементе — это ручка,
 * до которой дотягивается каждый слой по отдельности, и никакого
 * общего потолка над ними не построить. Владелец канала должен
 * иметь возможность сказать «громче этого — нельзя», и сказать так,
 * чтобы модератор физически не мог это обойти (docs/04). Единственный
 * способ — пропустить ВЕСЬ звук через один узел, который правит
 * только владелец.
 *
 * Отсюда граф из docs/04:
 *
 *     источники ──► gain(слой) ──► ducking ──► masterGain ──► destination
 *
 * DUCKING СТОИТ МЕЖДУ СЛОЕМ И МАСТЕРОМ, а не на мастере: приглушать
 * надо ФОН — музыку и видео, — а не то, ради чего приглушают.
 * Звук саундборда пойдёт мимо этого узла, прямо в мастер (4.4),
 * иначе он приглушал бы сам себя.
 *
 * ГРАФ ОДИН НА СТРАНИЦУ, и это требование, а не удобство: второй
 * AudioContext означал бы второй мастер-гейн, то есть вторую дорогу
 * мимо потолка.
 */
/**
 * Настоящий граф поверх AudioContext.
 *
 * Отдельный класс, а не функция на модуле: контекст надо где-то
 * держать, а тесту — подменять целиком.
 */
export class WebAudioGraph {
    context;
    master;
    /**
     * Узел приглушения фона.
     *
     * Между слоями и мастером: см. схему в шапке. Всё, что подключено
     * через него, приглушается на время звука саундборда; сам
     * саундборд пойдёт мимо.
     */
    duckNode;
    /** Когда вернуть фон обратно. */
    restore;
    /**
     * Потолок канала.
     *
     * Держится здесь, а не только на сервере: «мод не может превысить
     * потолок НИ ЧЕРЕЗ КАКОЙ ПУТЬ» — сообщение можно собрать мимо
     * панели, и последний рубеж стоит тут.
     */
    ceiling = 1;
    /**
     * Элементы, уже включённые в граф.
     *
     * createMediaElementSource можно звать для элемента РОВНО ОДИН
     * РАЗ: повторный вызов бросает исключение, и второй такой слой
     * уронил бы отрисовку всей сцены. Помнить об этом обязаны мы —
     * браузер не подсказывает.
     */
    connected = new WeakSet();
    constructor(context) {
        this.context = context;
        this.master = context.createGain();
        this.master.connect(context.destination);
        this.duckNode = context.createGain();
        this.duckNode.connect(this.master);
    }
    setMaster(master, ceiling) {
        this.ceiling = clamp(ceiling);
        this.master.gain.value = Math.min(clamp(master), this.ceiling);
    }
    duck(options) {
        // Прежний срок сбрасывается: два звука подряд не должны
        // возвращать фон посреди второго
        if (this.restore !== undefined) {
            clearTimeout(this.restore);
        }
        this.duckNode.gain.value = clamp(options.to);
        this.restore = setTimeout(() => {
            this.duckNode.gain.value = 1;
            this.restore = undefined;
        }, Math.max(0, options.holdMs));
    }
    connect(el) {
        // Через ducking, а не прямо в мастер: слой — это фон, и его
        // приглушают на время звука саундборда
        return this.route(el, this.duckNode);
    }
    connectDirect(el) {
        return this.route(el, this.master);
    }
    playBuffer(play) {
        let source;
        try {
            source = this.context.createBufferSource();
        }
        catch {
            // Контекст закрыт или браузер отказал. Вызывающий обязан
            // это пережить: у него есть запасной путь через <audio>
            return null;
        }
        source.buffer = play.buffer;
        const gain = this.context.createGain();
        gain.gain.value = clamp(play.gain);
        source.connect(gain);
        // В мастер напрямую: саундборд не приглушается вместе с фоном
        gain.connect(this.master);
        let done = false;
        const finish = () => {
            // Однократно: `stop()` вызовет onended, и без этой защиты
            // остановленный вручную звук отчитался бы дважды
            if (done)
                return;
            done = true;
            source.disconnect();
            gain.disconnect();
            play.onEnded();
        };
        source.onended = finish;
        source.start();
        return () => {
            try {
                source.stop();
            }
            catch {
                // Уже остановлен — обычное дело при закрытии страницы
            }
            finish();
        };
    }
    /**
     * Общая часть обоих подключений.
     *
     * Разница между ними ровно в одном — куда идёт выход, — и две
     * копии кода разошлись бы на первой правке. А правка тут дорогая:
     * забытый crossOrigin даёт не ошибку, а МОЛЧАНИЕ.
     */
    route(el, destination) {
        if (this.connected.has(el))
            return null;
        // ОБЯЗАТЕЛЬНО ДО createMediaElementSource. Медиа с чужого origin
        // (а наша раздача — именно чужой origin для страницы оверлея)
        // без CORS даёт МОЛЧАНИЕ: браузер подключает источник, граф
        // выглядит рабочим, а звука нет. Отладить это со стороны
        // страницы нечем
        if (el.crossOrigin === null) {
            /* eslint-disable-next-line no-param-reassign --
               Правило бережёт неизменяемость СОСТОЯНИЯ, а здесь меняется
               DOM: без crossOrigin граф получит от чужого origin тишину.
               Тот же случай, что в image.ts и video.ts. */
            el.crossOrigin = 'anonymous';
        }
        let source;
        try {
            source = this.context.createMediaElementSource(el);
        }
        catch {
            // Элемент уже подключён к другому контексту или браузер
            // отказал. Слой обязан продолжить работать молча, а не упасть
            return null;
        }
        this.connected.add(el);
        const gain = this.context.createGain();
        source.connect(gain);
        gain.connect(destination);
        return {
            setGain(value) {
                gain.gain.value = clamp(value);
            },
            release() {
                source.disconnect();
                gain.disconnect();
            },
        };
    }
}
/**
 * Текущий граф страницы.
 *
 * Модульная переменная, а не довод в mount: контракт виджета один
 * на все примитивы, и тащить в него звук ради одного `video`
 * значило бы объяснять его каждому следующему. Подменяется явно —
 * оверлей ставит настоящий при запуске, тест свой.
 */
let current = null;
export function setAudioGraph(graph) {
    current = graph;
}
export function audioGraph() {
    return current;
}
function clamp(value) {
    if (!Number.isFinite(value))
        return 0;
    return Math.min(1, Math.max(0, value));
}
//# sourceMappingURL=audio.js.map